import type {
  AdoptResult,
  FanoutMode,
  FanoutStudy,
  FanoutStudyResult,
  FanoutStudySummary,
} from "@crux/contracts";
import { api } from "../client.js";
import { problem } from "./errors.js";

/**
 * Query fan-out studies.
 */

export async function runFanoutStudy(
  workspaceId: string,
  body: { query: string; modes: FanoutMode[] },
): Promise<FanoutStudyResult | "unavailable"> {
  const response = await api.POST("/api/workspaces/{workspaceId}/fanout", {
    params: { path: { workspaceId } },
    body,
  });
  if (response.data === undefined) {
    const status = response.response.status;
    if (status === 501) return "unavailable";
    if (status === 502) {
      throw new Error(
        "The model could not be reached, so nothing was modelled. Nothing was saved and nothing was charged for.",
      );
    }
    throw new Error(problem(status, "model that fan-out"));
  }
  return response.data;
}

export async function fetchFanoutStudies(
  workspaceId: string,
): Promise<readonly FanoutStudySummary[]> {
  const response = await api.GET("/api/workspaces/{workspaceId}/fanout", {
    params: { path: { workspaceId } },
  });
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "load your fan-out history"));
  }
  return response.data.studies;
}

export async function fetchFanoutStudy(
  workspaceId: string,
  studyId: string,
): Promise<FanoutStudy> {
  const response = await api.GET("/api/workspaces/{workspaceId}/fanout/{studyId}", {
    params: { path: { workspaceId, studyId } },
  });
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "load that fan-out"));
  }
  return response.data;
}

export async function adoptPrompts(
  workspaceId: string,
  body: { reportId: string; texts: string[] },
): Promise<AdoptResult> {
  const response = await api.POST("/api/workspaces/{workspaceId}/fanout/adopt", {
    params: { path: { workspaceId } },
    body,
  });
  if (response.data === undefined) {
    throw new Error(problem(response.response.status, "add those questions"));
  }
  return response.data;
}
